<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  variant: {
    type: String,
    default: 'popover',
    validator: value => ['popover', 'inline'].includes(value),
  },
});

const { t } = useI18n();
const { accountId, accountScopedRoute } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');
const automations = useMapGetter('automations/getAutomations');

const agentConfiguredKey = `nervenet_agent_configured:${accountId.value}`;
const aiConfigured = ref(localStorage.getItem(agentConfiguredKey) === 'true');

const refreshAiConfigured = () => {
  aiConfigured.value = localStorage.getItem(agentConfiguredKey) === 'true';
};

const handleStorage = event => {
  if (event.key === agentConfiguredKey) refreshAiConfigured();
};

onMounted(() => {
  refreshAiConfigured();
  window.addEventListener('storage', handleStorage);
});
onUnmounted(() => window.removeEventListener('storage', handleStorage));

const setupSteps = computed(() => [
  {
    id: 'inbox',
    done: inboxes.value.length > 0,
    title: t('HOME.SETUP.INBOX_TITLE'),
    description: t('HOME.SETUP.INBOX_DESC'),
    cta: t('HOME.SETUP.INBOX_CTA'),
    route: accountScopedRoute('settings_inbox_new'),
    icon: 'i-lucide-inbox',
  },
  {
    id: 'ai_agent',
    done: aiConfigured.value,
    title: t('HOME.SETUP.AI_AGENT_TITLE'),
    description: t('HOME.SETUP.AI_AGENT_DESC'),
    cta: t('HOME.SETUP.AI_AGENT_CTA'),
    route: accountScopedRoute('nervenet_home'),
    icon: 'i-lucide-bot',
  },
  {
    id: 'automation',
    done: automations.value.length > 0,
    title: t('HOME.SETUP.AUTOMATION_TITLE'),
    description: t('HOME.SETUP.AUTOMATION_DESC'),
    cta: t('HOME.SETUP.AUTOMATION_CTA'),
    route: accountScopedRoute('automation_list'),
    icon: 'i-lucide-zap',
  },
]);

const doneCount = computed(() => setupSteps.value.filter(s => s.done).length);
const allDone = computed(() => doneCount.value === setupSteps.value.length);
const progressWidth = computed(
  () => `${(doneCount.value / setupSteps.value.length) * 100}%`
);

const [isOpen, toggleOpen] = useToggle();
const triggerRef = ref(null);
const popoverPosition = ref({ top: '0px', left: '0px' });

const updatePopoverPosition = () => {
  if (!triggerRef.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  popoverPosition.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
  };
};

const togglePopover = () => {
  if (!isOpen.value) updatePopoverPosition();
  toggleOpen();
};

const closePopover = () => {
  if (isOpen.value) isOpen.value = false;
};

const onStepClick = () => {
  if (props.variant === 'popover') closePopover();
};
</script>

<template>
  <!-- Popover variant: sidebar trigger + floating panel -->
  <div v-if="variant === 'popover'" v-show="!allDone" class="px-2">
    <button
      id="setupChecklistTrigger"
      ref="triggerRef"
      type="button"
      class="flex items-center w-full gap-2 px-2.5 py-1.5 rounded-lg h-8 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30 hover:outline-n-slate-6 transition-[outline-color] duration-150"
      :class="{ 'outline-n-slate-6': isOpen }"
      @click="togglePopover"
    >
      <span class="flex-shrink-0 i-lucide-list-checks size-3.5 text-n-brand" />
      <span class="flex-grow text-left text-n-slate-11 text-xs font-medium">
        {{
          t('HOME.SETUP.TRIGGER_LABEL', {
            done: doneCount,
            total: setupSteps.length,
          })
        }}
      </span>
      <span class="w-10 h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
        <span
          class="block h-full bg-n-brand rounded-full transition-all duration-700"
          :style="{ width: progressWidth }"
        />
      </span>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        v-on-click-outside="[
          closePopover,
          { ignore: ['#setupChecklistTrigger'] },
        ]"
        class="fixed z-[100]"
        :style="popoverPosition"
      >
        <section
          class="outline outline-1 outline-n-weak bg-n-solid-2 dark:bg-n-solid-2 rounded-xl p-4 w-80 shadow-xl"
        >
          <div class="flex items-center justify-between mb-4">
            <p class="text-sm font-semibold text-n-slate-12 m-0">
              {{ t('HOME.SETUP.POPOVER_HEADING') }}
            </p>
            <span class="text-xs font-medium text-n-slate-10 tabular-nums">
              {{
                t('HOME.SETUP.PROGRESS', {
                  done: doneCount,
                  total: setupSteps.length,
                })
              }}
            </span>
          </div>
          <ul class="flex flex-col gap-3 m-0 p-0 list-none">
            <li
              v-for="(step, i) in setupSteps"
              :key="step.id"
              class="rounded-lg border p-3 transition-colors duration-150"
              :class="
                step.done
                  ? 'bg-n-teal-3 border-n-teal-6'
                  : 'bg-n-alpha-1 border-dashed border-n-weak'
              "
            >
              <div class="flex items-start gap-3">
                <span
                  class="flex-shrink-0 size-6 rounded-full flex items-center justify-center text-xs font-semibold"
                  :class="
                    step.done
                      ? 'bg-n-teal-9 text-white'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  <span v-if="step.done" class="i-lucide-check size-3" />
                  <span v-else>{{ i + 1 }}</span>
                </span>
                <div class="flex-1 min-w-0">
                  <p
                    class="text-xs font-medium m-0"
                    :class="
                      step.done
                        ? 'text-n-slate-9 line-through'
                        : 'text-n-slate-12'
                    "
                  >
                    {{ step.title }}
                  </p>
                  <p
                    v-if="!step.done"
                    class="text-[11px] text-n-slate-10 mt-1 leading-relaxed mb-0"
                  >
                    {{ step.description }}
                  </p>
                  <RouterLink
                    v-if="!step.done"
                    :to="step.route"
                    class="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-n-amber-3 text-n-amber-11 border border-n-amber-6 hover:bg-n-amber-4 transition-colors duration-150 no-underline mt-2"
                    @click="onStepClick"
                  >
                    {{ step.cta }}
                    <span class="i-lucide-arrow-right size-3" />
                  </RouterLink>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </div>
    </Teleport>
  </div>

  <!-- Inline variant: home-page card -->
  <section
    v-else-if="!allDone"
    class="relative bg-gradient-to-br from-n-iris-2 via-n-solid-1 to-n-blue-2 dark:from-n-iris-3 dark:via-n-solid-2 dark:to-n-blue-3 rounded-2xl p-8 border border-n-iris-5 shadow-xl overflow-hidden"
  >
    <div
      class="absolute -top-24 -right-24 size-64 rounded-full bg-n-brand/15 blur-3xl pointer-events-none"
    />
    <div
      class="absolute -bottom-24 -left-24 size-64 rounded-full bg-n-iris-9/10 blur-3xl pointer-events-none"
    />

    <div class="relative flex items-center justify-between mb-8 gap-4">
      <div class="flex items-center gap-4">
        <div
          class="size-12 rounded-xl bg-n-brand flex items-center justify-center shadow-lg shadow-n-brand/30"
        >
          <span class="i-lucide-rocket size-6 text-white" />
        </div>
        <div>
          <p class="text-xl font-bold text-n-slate-12 m-0 tracking-tight">
            {{ t('HOME.SETUP.TITLE') }}
          </p>
          <p class="text-sm text-n-slate-11 mt-0.5 mb-0">
            {{ t('HOME.SETUP.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <div class="flex flex-col items-end gap-2 flex-shrink-0">
        <span class="text-sm font-semibold text-n-slate-12 tabular-nums">
          {{
            t('HOME.SETUP.PROGRESS', {
              done: doneCount,
              total: setupSteps.length,
            })
          }}
        </span>
        <div class="w-40 h-2.5 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full bg-n-brand rounded-full transition-all duration-700"
            :style="{ width: progressWidth }"
          />
        </div>
      </div>
    </div>
    <div class="relative grid grid-cols-1 md:grid-cols-3 gap-6">
      <RouterLink
        v-for="step in setupSteps"
        :key="step.id"
        :to="step.route"
        class="group rounded-xl border-2 p-5 transition-all duration-200 flex flex-col gap-4 no-underline"
        :class="
          step.done
            ? 'bg-n-teal-3 border-n-teal-6 opacity-75 hover:opacity-90'
            : 'bg-n-solid-1 border-n-brand shadow-md hover:shadow-2xl hover:-translate-y-1 ring-2 ring-n-brand/15 hover:ring-n-brand/40'
        "
      >
        <!-- Illustration zone -->
        <div
          class="relative h-32 w-full rounded-lg flex items-center justify-center overflow-hidden"
          :class="step.done ? 'opacity-60' : ''"
        >
          <!-- Connect inbox: stacked channel cards -->
          <template v-if="step.id === 'inbox'">
            <div class="flex items-center -space-x-2">
              <div
                class="size-12 rounded-xl bg-channel-whatsapp flex items-center justify-center shadow-md -rotate-12 z-10"
              >
                <span class="i-ri-whatsapp-fill size-7 text-white" />
              </div>
              <div
                class="size-12 rounded-xl bg-gradient-to-br from-channel-messenger-from to-channel-messenger-to flex items-center justify-center shadow-md -rotate-3 z-20"
              >
                <span class="i-ri-messenger-fill size-7 text-white" />
              </div>
              <div
                class="size-12 rounded-xl bg-white border border-n-weak flex items-center justify-center shadow-md rotate-3 z-30"
              >
                <span class="i-ri-mail-fill size-7 text-channel-gmail" />
              </div>
              <div
                class="size-12 rounded-xl bg-gradient-to-br from-channel-instagram-from via-channel-instagram-via to-channel-instagram-to flex items-center justify-center shadow-md rotate-12 z-40"
              >
                <span class="i-ri-instagram-fill size-7 text-white" />
              </div>
            </div>
          </template>

          <!-- Automation: lightning bolt with workflow nodes -->
          <template v-if="step.id === 'automation'">
            <div class="relative w-32 h-20 flex items-center justify-center">
              <div
                class="size-16 rounded-2xl bg-n-amber-9 flex items-center justify-center shadow-md"
              >
                <span class="i-lucide-zap size-9 text-white" />
              </div>
              <div
                class="absolute top-2 left-4 size-6 rounded-lg bg-n-iris-9 flex items-center justify-center shadow-md -rotate-12"
              >
                <span class="i-lucide-git-branch size-3.5 text-white" />
              </div>
              <div
                class="absolute bottom-2 right-4 size-7 rounded-full bg-n-teal-9 flex items-center justify-center shadow-md ring-2 ring-white"
              >
                <span class="i-lucide-check size-4 text-white" />
              </div>
            </div>
          </template>

          <!-- AI agent: bot with sparkles -->
          <template v-else-if="step.id === 'ai_agent'">
            <div
              class="size-16 rounded-2xl bg-n-iris-9 flex items-center justify-center shadow-md"
            >
              <span class="i-lucide-bot size-9 text-white" />
            </div>
            <span
              class="absolute top-4 left-1/3 i-lucide-sparkles size-5 text-n-amber-9"
            />
            <span
              class="absolute bottom-5 right-1/3 i-lucide-sparkles size-4 text-n-amber-10"
            />
            <span
              class="absolute top-10 right-8 size-2 rounded-full bg-n-iris-7"
            />
            <span
              class="absolute bottom-8 left-12 size-1.5 rounded-full bg-n-iris-5"
            />
          </template>
        </div>

        <!-- Text + CTA hint -->
        <div class="flex-1 min-w-0 flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <span
              v-if="step.done"
              class="flex-shrink-0 size-5 rounded-full bg-n-teal-9 flex items-center justify-center"
            >
              <span class="i-lucide-check size-3 text-white" />
            </span>
            <p
              class="text-base font-semibold m-0"
              :class="
                step.done ? 'text-n-slate-9 line-through' : 'text-n-slate-12'
              "
            >
              {{ step.title }}
            </p>
          </div>
          <p class="text-xs text-n-slate-10 leading-relaxed mb-0">
            {{ step.description }}
          </p>
          <span
            v-if="!step.done"
            class="inline-flex items-center gap-1.5 text-sm font-semibold text-n-brand mt-2 transition-all duration-150 group-hover:gap-3"
          >
            {{ step.cta }}
            <span class="i-lucide-arrow-right size-4" />
          </span>
        </div>
      </RouterLink>
    </div>
  </section>
</template>
